/**
 * The registry of feature flags a user may set on themselves, shared with the
 * server: the keys, so `PATCH /api/user` can reject unknown ones (`featureFlags`
 * is a Mixed field which the Settings UI saves wholesale), and each flag's plan
 * tier and default, so both sides answer "does this user have this feature?"
 * the same way. `featureFlags.ts` adds the descriptions and forum links the
 * Settings page renders.
 */
export const userFlagSpecs = /** @type {const} */ ({
  showTransliterations: { availableTo: "free", defaultValue: true },
  askAI: { availableTo: "premium" },
  contextTranslations: { availableTo: "premium", defaultValue: true },
  betterAIModel: { availableTo: "plus", defaultValue: true },
  disableMarginButtons: { availableTo: "free" },
  explainTitleAndAuthor: { availableTo: "premium" },
});

export const userFlagKeys = Object.keys(userFlagSpecs);

/**
 * The subscription plans each tier admits. `free` admits everyone; `god` admits
 * nobody by plan - no flag uses that tier, and one that did would be granted by
 * the god check on the route rather than by a flag the user can write.
 */
const plansByTier = {
  free: undefined,
  premium: ["standard", "plus", "freeForever"],
  plus: ["plus", "freeForever"],
  god: [],
};

/**
 * Whether a user has a feature switched on: an eligible plan **and** the flag's
 * own value, defaulting to the flag's `defaultValue` when they have never
 * toggled it.
 *
 * The plan half is not overridable by the flag half. Both are needed: reading
 * the flag alone lets any account select a paid feature by writing it (the
 * Settings UI saves `featureFlags` straight through), while requiring the flag
 * alone would switch a default-true feature off for every eligible user who has
 * never opened Settings.
 *
 * @param {{subscriptionPlan?: string, featureFlags?: {[key: string]: any}} | null | undefined} user
 * @param {string} key - a `userFlagSpecs` key
 * @returns {boolean}
 */
export const hasFeature = (user, key) => {
  const spec = userFlagSpecs[key];
  if (!spec) {
    return false;
  }
  const plans = plansByTier[spec.availableTo];
  if (plans && !plans.includes(user?.subscriptionPlan)) {
    return false;
  }
  return Boolean(user?.featureFlags?.[key] ?? spec.defaultValue ?? false);
};
